import React, { useEffect, useRef, useState } from "react";
import { TextAnswerFormat } from "../../../../types/Survey";
import "./TextQuestion.css"
import "text-security/text-security.css"
import SurveyInput from "../../Components/SurveyInput/SurveyInput";
import { SurveyQuestionProps } from "../../Components/SurveyQuestion/SurveyQuestion";
import SurveyFormItem from "../../Components/SurveyFormItem/SurveyFormItem";
import autosize from "autosize"
import useTranslate from "../../../../hooks/useTranslate";
import { QuestionResultModel } from "../../../../types/SurveyResult";

export default function (props: SurveyQuestionProps) {
    let [validationError, setValidationError] = useState<boolean>(false);
    let textAreaRef = useRef<HTMLTextAreaElement>(null);
    let answerFormat = props.answerFormat as TextAnswerFormat;
    let currentResult = props.result as QuestionResultModel;
    const currentAnswer = currentResult.Answers[0] || "";
    let defaultPlaceholder = useTranslate(["default-text-placeholder"])["default-text-placeholder"];

    function validateAnswer(answer: string) {
        if (!answerFormat.validationPattern) return true;
        if (props.optional && answer === "") return true;
        //follows same pattern as ng-pattern, which is what was used originally:
        //https://docs.angularjs.org/api/ng/directive/ngPattern
        return new RegExp(`^${answerFormat.validationPattern}$`).test(answer);
    }

    const answerChanged = (answer: string) => {
        if (!validateAnswer(answer)) {
            props.onResultChange({ ...props.result, Answers: [answer], invalid: true } as QuestionResultModel);
            return;
        }
        setValidationError(false);
        props.onResultChange({ ...props.result, Answers: [answer], invalid: false } as QuestionResultModel);
    };

    function getInputMode() {
        switch (answerFormat.keyboardType) {
            case 'Number':
                return 'numeric';
            case 'Phone':
                return 'tel';
            case 'Email':
                return 'email';
            default:
                return 'text';
        }
    }

    function getMaxLength() {
        return answerFormat.maximumLength || 524288;
    }

    function updateValidationError(answer: string) {
        if (!validateAnswer(answer)) {
            setValidationError(true);
        }
    }

    if (textAreaRef.current) {
        autosize(textAreaRef.current);
    }

    let classes = ["text-question"];
    if (props.variant === "v2") {
        classes.push("text-question-v2");
    }

    useEffect(() => {
        // set initial validation error if the answer is not valid
        if (currentAnswer) {
            answerChanged(currentAnswer);
            updateValidationError(currentAnswer);
        }
    }, []);

    return <SurveyFormItem
        className={classes.join(" ")}
        bottomBorder
        topBorder
        label={props.label}
        labelFor={props.inputIdForLabel}
        validationError={validationError ? answerFormat.invalidMessage : undefined}
        labelPosition={answerFormat.multipleLines ? "top" : props.labelPosition}
        variant={props.variant}
        useFieldsetFormat={props.useFieldsetFormat}
        index={props.index}>
        {answerFormat.multipleLines &&
            <textarea ref={textAreaRef} id={props.inputIdForLabel}
                value={currentAnswer}
                placeholder={props.placeholder || defaultPlaceholder}
                onChange={(e) => answerChanged(e.target.value)}
                inputMode={getInputMode()}
                maxLength={getMaxLength()}
                autoComplete={props.autoComplete}
                onBlur={(e) => updateValidationError(e.target.value)}
                className={answerFormat.isSecureTextEntry && currentAnswer ? "secure-text-entry" : ""}></textarea>
        }
        {!answerFormat.multipleLines &&
            <SurveyInput id={props.inputIdForLabel}
                aria-describedby={validationError ? `${props.inputIdForLabel + "-error"}` : ''}
                value={currentAnswer}
                type={answerFormat.keyboardType === 'Email' ? 'email' : 'text'}
                placeholder={props.placeholder || defaultPlaceholder}
                onChange={(e) => answerChanged(e.target.value)}
                onEnterPressed={props.onEnterPressed}
                inputMode={getInputMode()}
                maxLength={getMaxLength()}
                autoComplete={props.autoComplete}
                onBlur={(e) => updateValidationError(e.target.value)}
                className={answerFormat.isSecureTextEntry && currentAnswer ? "secure-text-entry" : ""}
                variant={props.variant} />
        }
    </SurveyFormItem>
}